import TypographyParagraph from '../../typography/TypographyParagraph'
import { Tooltip, TooltipRefProps } from 'react-tooltip'
import { Suspense, useRef } from 'react'
import { asyncTruncateStringInMiddle } from '#app/utils/misc.tsx'
import { Await } from '@remix-run/react'

export default function OffchainSaleTemplate({
	offchainSale,
}: {
	offchainSale: any
}) {
	const tooltipCopyDate = useRef<TooltipRefProps>(null)
	const tooltipCopyStartDate = useRef<TooltipRefProps>(null)
	const tooltipCopyEndDate = useRef<TooltipRefProps>(null)
	const tooltipCopyBuyerAddress = useRef<TooltipRefProps>(null)
	const tooltipCopySellerAddress = useRef<TooltipRefProps>(null)
	const tooltipCopyAmount = useRef<TooltipRefProps>(null)
	const tooltipCopyCurrency = useRef<TooltipRefProps>(null)
	const tooltipCopyComment = useRef<TooltipRefProps>(null)
	const tooltipCopySignature = useRef<TooltipRefProps>(null)

	return (
		<>
			{offchainSale.properties.date && (
				<div
					id="date"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:border-r"
					onClick={() => {
						navigator.clipboard.writeText(offchainSale.properties.date)
						tooltipCopyDate.current?.open({
							anchorSelect: '#date',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyDate.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyDate} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Date:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{offchainSale.properties.date}
					</TypographyParagraph>
				</div>
			)}
			{offchainSale.properties.startDate && offchainSale.properties.endDate && (
				<div className="flex flex-col sm:flex-row">
					<div
						id="startDate"
						className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
						onClick={() => {
							navigator.clipboard.writeText(offchainSale.properties.startDate)
							tooltipCopyStartDate.current?.open({
								anchorSelect: '#startDate',
								content: 'Copied!',
							})
							setTimeout(() => {
								tooltipCopyStartDate.current?.close()
							}, 2000)
						}}
					>
						<Tooltip imperativeModeOnly ref={tooltipCopyStartDate} />
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							Start date:
						</TypographyParagraph>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{offchainSale.properties.startDate}
						</TypographyParagraph>
					</div>
					<div
						id="endDate"
						className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2"
						onClick={() => {
							navigator.clipboard.writeText(offchainSale.properties.endDate)
							tooltipCopyEndDate.current?.open({
								anchorSelect: '#endDate',
								content: 'Copied!',
							})
							setTimeout(() => {
								tooltipCopyEndDate.current?.close()
							}, 2000)
						}}
					>
						<Tooltip imperativeModeOnly ref={tooltipCopyEndDate} />
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							End date:
						</TypographyParagraph>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{offchainSale.properties.endDate}
						</TypographyParagraph>
					</div>
				</div>
			)}
			<div className="flex flex-col sm:flex-row">
				<div
					id="buyerAddress"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
					onClick={() => {
						navigator.clipboard.writeText(
							offchainSale.properties.receiverAddress,
						)
						tooltipCopyBuyerAddress.current?.open({
							anchorSelect: '#buyerAddress',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyBuyerAddress.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyBuyerAddress} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Buyer Address:
					</TypographyParagraph>
					<Suspense
						fallback={
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all"
							>
								...
							</TypographyParagraph>
						}
					>
						<Await
							resolve={(async () =>
								await asyncTruncateStringInMiddle(
									offchainSale.properties.receiverAddress,
								))()}
						>
							{(receiverAddress: string) => (
								<TypographyParagraph
									size="medium"
									additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all"
								>
									{receiverAddress}
								</TypographyParagraph>
							)}
						</Await>
					</Suspense>
				</div>
				<div
					id="sellerAddress"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2"
					onClick={() => {
						navigator.clipboard.writeText(offchainSale.properties.ownerAddress)
						tooltipCopySellerAddress.current?.open({
							anchorSelect: '#sellerAddress',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopySellerAddress.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopySellerAddress} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Seller Address:
					</TypographyParagraph>
					<Suspense
						fallback={
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all"
							>
								...
							</TypographyParagraph>
						}
					>
						<Await
							resolve={(async () =>
								await asyncTruncateStringInMiddle(
									offchainSale.properties.ownerAddress,
								))()}
						>
							{(ownerAddress: string) => (
								<TypographyParagraph
									size="medium"
									additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all"
								>
									{ownerAddress}
								</TypographyParagraph>
							)}
						</Await>
					</Suspense>
				</div>
			</div>
			<div className="flex flex-col sm:flex-row">
				<div
					id="amount"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
					onClick={() => {
						navigator.clipboard.writeText(offchainSale.properties.amount)
						tooltipCopyAmount.current?.open({
							anchorSelect: '#amount',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyAmount.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyAmount} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Amount:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{offchainSale.properties.amount}
					</TypographyParagraph>
				</div>
				<div
					id="currency"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2"
					onClick={() => {
						navigator.clipboard.writeText(offchainSale.properties.currency)
						tooltipCopyCurrency.current?.open({
							anchorSelect: '#currency',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyCurrency.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyCurrency} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Currency:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{offchainSale.properties.currency}
					</TypographyParagraph>
				</div>
			</div>
			<div
				id="comment"
				className="flex flex-row border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark"
				onClick={() => {
					navigator.clipboard.writeText(offchainSale.properties.description)
					tooltipCopyComment.current?.open({
						anchorSelect: '#currency',
						content: 'Copied!',
					})
					setTimeout(() => {
						tooltipCopyComment.current?.close()
					}, 2000)
				}}
			>
				<Tooltip imperativeModeOnly ref={tooltipCopyComment} />
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Comment:
				</TypographyParagraph>
				<TypographyParagraph
					size="medium"
					additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark"
				>
					{offchainSale.properties.description}
				</TypographyParagraph>
			</div>
			<div
				id="signature"
				className="flex flex-row p-4 hover:bg-uiSecondaryLight dark:hover:bg-uiSecondaryDark"
				onClick={() => {
					navigator.clipboard.writeText(offchainSale.signature)
					tooltipCopySignature.current?.open({
						anchorSelect: '#signature',
						content: 'Copied!',
					})
					setTimeout(() => {
						tooltipCopySignature.current?.close()
					}, 2000)
				}}
			>
				<Tooltip imperativeModeOnly ref={tooltipCopySignature} />
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Signature:
				</TypographyParagraph>
				<TypographyParagraph
					size="medium"
					additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark break-all"
				>
					{offchainSale.signature}
				</TypographyParagraph>
			</div>
		</>
	)
}
